import React, { useState, useEffect, useMemo, useRef } from 'react';
import Layout from '../components/Layout';
import { api, formatApiErrorDetail } from '../utils/api';
import { Plus, Edit, Trash2, Save, X, Image as ImageIcon, ArrowUp, ArrowDown, ArrowUpDown, Filter, Check } from 'lucide-react';
import { toast } from 'sonner';
import BACKEND_BASE from '../utils/backend';

const Icons = () => {
  const [icons, setIcons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: 'action',
    image_url: '',
    description: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  // Sort + filter state
  const [sortDir, setSortDir] = useState('none'); // 'none' | 'asc' | 'desc'
  const [activeCategories, setActiveCategories] = useState(new Set()); // empty = all
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef(null);

  const categories = [
    { value: 'action', label: 'Action' },
    { value: 'product', label: 'Produit' },
    { value: 'equipment', label: 'Equipement' },
    { value: 'surface', label: 'Surface' },
    { value: 'temperature', label: 'Temperature' },
    { value: 'time', label: 'Temps' },
    { value: 'frequency', label: 'Frequence' },
    { value: 'control', label: 'Controle' },
    { value: 'safety', label: 'Securite' },
    { value: 'zone', label: 'Zone' },
  ];

  useEffect(() => {
    loadIcons();
  }, []);

  // Close the category-filter dropdown on outside click
  useEffect(() => {
    if (!filterOpen) return;
    const handler = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filterOpen]);

  // Dynamic list of categories actually present in the data (sorted by label)
  const availableCategories = useMemo(() => {
    const seen = new Set();
    const result = [];
    icons.forEach((i) => {
      const cat = i.category || 'autre';
      if (!seen.has(cat)) {
        seen.add(cat);
        const known = categories.find((c) => c.value === cat);
        result.push({ value: cat, label: known ? known.label : cat });
      }
    });
    return result.sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [icons]);

  // Apply filter + sort for the visible rows
  const visibleIcons = useMemo(() => {
    let rows = icons;
    if (activeCategories.size > 0) {
      rows = rows.filter((i) => activeCategories.has(i.category || 'autre'));
    }
    if (sortDir !== 'none') {
      const factor = sortDir === 'asc' ? 1 : -1;
      rows = [...rows].sort((a, b) =>
        factor * (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
      );
    }
    return rows;
  }, [icons, activeCategories, sortDir]);

  const cycleSort = () => {
    setSortDir((d) => (d === 'none' ? 'asc' : d === 'asc' ? 'desc' : 'none'));
  };

  const toggleCategoryFilter = (value) => {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const clearCategoryFilter = () => setActiveCategories(new Set());

  const loadIcons = async () => {
    try {
      const { data } = await api.get('/icons');
      setIcons(data);
    } catch (error) {
      toast.error('Echec du chargement des icones');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleUploadImage = async () => {
    if (!selectedFile) {
      toast.error('Veuillez selectionner un fichier');
      return null;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const { data } = await api.post('/icons/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploading(false);
      return data.url;
    } catch (error) {
      setUploading(false);
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let imageUrl = formData.image_url;

    if (selectedFile) {
      imageUrl = await handleUploadImage();
      if (!imageUrl) return;
    }

    try {
      const payload = { ...formData, image_url: imageUrl };

      if (editingId) {
        await api.put(`/icons/${editingId}`, payload);
        toast.success('Icone modifiee');
      } else {
        await api.post('/icons', payload);
        toast.success('Icone creee');
      }
      resetForm();
      loadIcons();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleEdit = (icon) => {
    setFormData({
      name: icon.name,
      category: icon.category || 'action',
      image_url: icon.image_url || '',
      description: icon.description || '',
    });
    setPreviewUrl(icon.image_url ? BACKEND_BASE + icon.image_url : '');
    setEditingId(icon.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette icone ?')) return;
    try {
      await api.delete(`/icons/${id}`);
      toast.success('Icone supprimee');
      loadIcons();
    } catch (error) {
      toast.error('Echec de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', category: 'action', image_url: '', description: '' });
    setSelectedFile(null);
    setPreviewUrl('');
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Chargement...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="icons-page">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold" style={{ color: '#0E2D43', fontFamily: 'Work Sans, sans-serif' }}>
            Gestion des Icones
          </h1>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              data-testid="add-icon-button"
              className="flex items-center space-x-2 px-4 py-2 rounded font-medium"
              style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}
            >
              <Plus size={20} />
              <span>Ajouter Icone</span>
            </button>
          )}
        </div>

        {showForm && (
          <div className="bg-white rounded-lg shadow p-6 border mb-8" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: '#0E2D43' }}>
              {editingId ? 'Modifier Icone' : 'Nouvelle Icone'}
            </h2>
            <form onSubmit={handleSubmit} data-testid="icon-form">
              <div className="grid grid-cols-2 gap-6 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>
                    Nom *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="icon-name-input"
                    className="w-full px-4 py-2 border rounded"
                    style={{ borderColor: '#D1D5DB' }}
                    placeholder="ex: Gants, Spray, Horloge"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>
                    Categorie *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    data-testid="icon-category-select"
                    className="w-full px-4 py-2 border rounded"
                    style={{ borderColor: '#D1D5DB' }}
                    required
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>
                    Image de l&apos;icone
                  </label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                      onChange={handleFileChange}
                      className="flex-1 px-4 py-2 border rounded"
                      style={{ borderColor: '#D1D5DB' }}
                    />
                    {previewUrl && (
                      <div className="w-16 h-16 border rounded p-1" style={{ borderColor: '#D1D5DB' }}>
                        <img src={previewUrl} alt="Preview" className="w-full h-full object-contain" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs mt-1" style={{ color: '#6B7280' }}>
                    Formats acceptes: PNG, JPG, SVG, WEBP
                  </p>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>
                    Description
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="icon-description-input"
                    className="w-full px-4 py-2 border rounded"
                    style={{ borderColor: '#D1D5DB' }}
                    placeholder="Description optionnelle de l'icone"
                  />
                </div>
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={uploading}
                  data-testid="save-icon-button"
                  className="flex items-center space-x-2 px-4 py-2 rounded font-medium"
                  style={{ backgroundColor: uploading ? '#6B7280' : '#0E2D43', color: '#FFFFFF' }}
                >
                  <Save size={18} />
                  <span>{uploading ? 'Upload...' : 'Enregistrer'}</span>
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex items-center space-x-2 px-4 py-2 rounded border"
                  style={{ borderColor: '#D1D5DB', color: '#0E2D43' }}
                >
                  <X size={18} />
                  <span>Annuler</span>
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden border" style={{ borderColor: '#E5E7EB' }}>
          <table className="w-full">
            <thead style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium">Apercu</th>
                <th className="px-6 py-3 text-left text-sm font-medium">
                  <button
                    type="button"
                    onClick={cycleSort}
                    data-testid="sort-name-header"
                    className="inline-flex items-center gap-1.5 hover:opacity-90 focus:outline-none"
                  >
                    <span>Nom</span>
                    {sortDir === 'asc' && <ArrowUp size={14} data-testid="sort-name-asc-icon" />}
                    {sortDir === 'desc' && <ArrowDown size={14} data-testid="sort-name-desc-icon" />}
                    {sortDir === 'none' && <ArrowUpDown size={14} style={{ opacity: 0.55 }} />}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium">
                  <div ref={filterRef} className="relative inline-block">
                    <button
                      type="button"
                      onClick={() => setFilterOpen((v) => !v)}
                      data-testid="filter-category-header"
                      className="inline-flex items-center gap-1.5 hover:opacity-90 focus:outline-none"
                    >
                      <span>Categorie</span>
                      <Filter
                        size={14}
                        data-testid="filter-category-icon"
                        style={{
                          color: activeCategories.size > 0 ? '#FBBF24' : '#FFFFFF',
                          fill: activeCategories.size > 0 ? '#FBBF24' : 'none',
                          opacity: activeCategories.size > 0 ? 1 : 0.55,
                        }}
                      />
                      {activeCategories.size > 0 && (
                        <span
                          data-testid="filter-category-badge"
                          className="ml-1 text-[10px] font-bold rounded-full px-1.5 py-0.5"
                          style={{ backgroundColor: '#FBBF24', color: '#0E2D43' }}
                        >
                          {activeCategories.size}
                        </span>
                      )}
                    </button>
                    {filterOpen && (
                      <div
                        data-testid="filter-category-dropdown"
                        className="absolute z-30 left-0 mt-2 w-60 rounded-lg shadow-lg border py-1"
                        style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E7EB', color: '#0E2D43' }}
                      >
                        <button
                          type="button"
                          onClick={clearCategoryFilter}
                          data-testid="filter-clear-button"
                          className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50"
                        >
                          <span className="font-medium">Toutes les categories</span>
                          {activeCategories.size === 0 && <Check size={14} />}
                        </button>
                        <div className="border-t my-1" style={{ borderColor: '#E5E7EB' }} />
                        <div className="max-h-72 overflow-y-auto">
                          {availableCategories.map((cat) => {
                            const active = activeCategories.has(cat.value);
                            return (
                              <button
                                type="button"
                                key={cat.value}
                                onClick={() => toggleCategoryFilter(cat.value)}
                                data-testid={`filter-category-option-${cat.value}`}
                                className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50"
                              >
                                <span>{cat.label}</span>
                                {active && <Check size={14} style={{ color: '#0E2D43' }} />}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleIcons.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center" style={{ color: '#6B7280' }}>
                    {icons.length === 0
                      ? 'Aucune icone pour le moment. Ajoutez votre premiere icone !'
                      : 'Aucune icone ne correspond aux filtres actifs.'}
                  </td>
                </tr>
              ) : (
                visibleIcons.map((icon) => (
                  <tr key={icon.id} data-testid={`icon-row-${icon.id}`} className="border-t" style={{ borderColor: '#E5E7EB' }}>
                    <td className="px-6 py-4">
                      {icon.image_url ? (
                        <div className="w-12 h-12 border rounded p-1" style={{ borderColor: '#D1D5DB' }}>
                          <img
                            src={BACKEND_BASE + icon.image_url}
                            alt={icon.name}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="w-12 h-12 border rounded flex items-center justify-center" style={{ borderColor: '#D1D5DB', backgroundColor: '#F3F4F6' }}>
                          <ImageIcon size={20} style={{ color: '#9CA3AF' }} />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4" style={{ color: '#0E2D43' }}>
                      <span className="font-medium">{icon.name}</span>
                    </td>
                    <td className="px-6 py-4" style={{ color: '#6B7280' }}>
                      <span
                        className="px-3 py-1 rounded text-sm"
                        style={{
                          backgroundColor:
                            { action: '#DBEAFE', product: '#D1FAE5', equipment: '#FEF3C7', surface: '#FCE7F3',
                              temperature: '#FEE2E2', time: '#E0F2FE', frequency: '#EDE9FE', control: '#FFF7ED',
                              safety: '#FEE2E2', zone: '#ECFDF5' }[icon.category] || '#F3F4F6',
                          color:
                            { action: '#1E40AF', product: '#065F46', equipment: '#92400E', surface: '#9D174D',
                              temperature: '#991B1B', time: '#0E7490', frequency: '#5B21B6', control: '#9A3412',
                              safety: '#991B1B', zone: '#047857' }[icon.category] || '#374151',
                        }}
                      >
                        {categories.find((c) => c.value === icon.category)?.label || icon.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleEdit(icon)}
                        data-testid={`edit-icon-${icon.id}`}
                        className="inline-flex items-center px-3 py-1 rounded mr-2"
                        style={{ backgroundColor: '#F3F4F6', color: '#0E2D43' }}
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(icon.id)}
                        data-testid={`delete-icon-${icon.id}`}
                        className="inline-flex items-center px-3 py-1 rounded"
                        style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
};

export default Icons;
